import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  Router,
} from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';
import { getAppName } from '../state/_selectors/application.selector';
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root',
})
export class RouteService {
  private static readonly ROOT = '/';
  private static readonly APPS = '/apps';
  private static readonly SEGMENT_APPS = 'apps';
  private static readonly NOT_FOUND = -1;
  paramMap: any = {};
  queryParams: any = {};
  previousUrl:string = '';
  currentUrl:string = '';
  historyStack: string[] = []; 
  popstate: boolean = false;
  isCustomUrl: boolean = false;
  ignatiusDomains: Array<string> = [
    'localhost',
    'dev.ignatius.io',
    'staging.ignatius.io',
    'ignatius.io',
  ];


  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public location: Location,
    public title: Title,
    public store: Store,
    private commonService: CommonService
  ) {
    this.getApiUrlCheck();
    this.listenToPopState(); 
    this.change()
      .subscribe(() => {
        let route = this.route;
        while (route.firstChild) route = route.firstChild;
        route.params.subscribe((params) => {
          const routeParams = route.parent?.snapshot?.params?.appId ? route.parent.snapshot.params :
                                    route.parent?.parent?.snapshot?.params?.appId ? route.parent?.parent?.snapshot?.params :
                                        route.parent?.parent?.parent?.snapshot?.params;
          this.paramMap = { ...params, ...routeParams };
        });
        route.queryParams.subscribe((query) => {
          this.queryParams = {
            ...query,
            ...route.parent?.snapshot.queryParams,
          };
        });
      });
      this.currentUrl = this.router.url;
      this.change().subscribe((event: any) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        if(this.isCustomUrl){
          this.commonService.setOGSupportActive(false) 
        } else if(this.currentUrl.includes('/auth')){
          this.commonService.setOGSupportActive(true);  
        }
        if((this.currentUrl.includes('/apps') || this.currentUrl.includes('/company') || this.currentUrl.includes('/clients')) && !this.historyStack.includes(this.currentUrl) && !this.popstate) this.historyStack.push(this.currentUrl);
        this.popstate = false;
      });
      
  }
  private listenToPopState() {
    window.addEventListener('popstate', () => {
      if(this.historyStack.length > 1){
        this.historyStack.pop();
      }
      this.popstate = true;
    });
  }

  /**
   * get api url's
   */

  private getApiUrlCheck = () =>{
    let url = window.location.hostname;
    this.isCustomUrl =  !this.ignatiusDomains.includes(url);
  }

  /**
   * Router change event
   * @returns observable
   */
  change = () =>
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

  /**
   * Make sure on each url defined, id of application should be defined under "appId"
   * Look application.routing.ts
   * @returns Application id
   */
  getApplicationKey = () => {
    // Primary source: resolved route params
    const appIdFromParams = this.paramMap?.appId;
    if (appIdFromParams && appIdFromParams !== 'apps') {
      return appIdFromParams;
    }

    // Fallback: derive from URL when params are missing or incorrect
    const url = this.router?.url || '';
    const derived = this.extractAppIdFromUrl(url);
    return derived || appIdFromParams;
  };

  getApplicationName = async () => {
    this.changeTitle('Loading...');
    // Skip store lookups on auth routes where feature slices may not be loaded yet
    if (this.currentRoute().includes('/auth')) {
      this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' }`);
      return;
    }
    var appId = (this.paramMap.appId);
    if (appId) {
      this.store
        .pipe(
          select(getAppName),
          filter((app: any) => !!app && !!app[appId]),
          map((app: any) => app[appId])
        )
        .subscribe((applicationName) => {
          if (applicationName != '') {
            if (this.currentRoute().includes('reports')) {
              this.changeTitle(applicationName + ' | Reports');
            } else if (this.currentRoute().includes('imports')) {
              this.changeTitle(applicationName + ' | Imports');
            } else if (this.currentRoute().includes('fields')) {
              this.changeTitle(applicationName + ' | Fields');
            } else if (this.currentRoute().includes('relations')) {
              this.changeTitle(applicationName + ' | Relations');
            } else if (this.currentRoute().includes('forms')) {
              this.changeTitle(applicationName + ' | Forms');
            } else if (this.currentRoute().includes('notifications')) {
              this.changeTitle(applicationName + ' | Notifications');
            } else if (this.currentRoute().includes('settings')) {
              this.changeTitle(applicationName + ' | Settings');
            } else if (this.currentRoute().includes('tables')) {
              this.changeTitle(applicationName + ' | Tables');
            } else if (this.currentRoute().includes('apps')) {
              this.changeTitle(applicationName);
            } else {
              this.changeTitle(applicationName);
            }
          }
        });
    } else if (!this.currentRoute().includes('auth')) {
      if (this.currentRoute().includes('profile')) {
        this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' } | Profile`);
      } else if (this.currentRoute().includes('company')) {
        this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' } | Company`);
      } else if (this.currentRoute().includes('clients')) {
        this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' } | Clients`);
      } else if (this.currentRoute().includes('apps')) {
        this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' } | Apps`);
      } else {
        this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' }`);
      }
    } else {
      this.changeTitle(`${ this.commonService.isIgnatiusThemeApplied ? 'ignatius': 'OpenGov' }`);
    }
  };

  /**
   * Make sure on each url defined, id of role should be defined under "roleId"
   * Look application.routing.ts
   * @returns Role id
   */
  getRoleKey = () => {
    return (this.paramMap.roleId);
  };

  /**
   * Get query param *id*
   */
  get queryId() {
    return Number(this.queryParams.id);
  }

  /**
   * Make sure on each url defined, id of application should be defined under "tableId"
   * @returns table id from URL
   */
  getTableKey = () => this.paramMap.tableId;

  /**
   * Make sure on each url defined, id of page should be defined under "pageId"
   * @returns page id from URL
   */
  getPageKey = () => (this.paramMap.pageId);

  /**
   * Make sure on each url defined, id of theme should be defined under "themeId"
   * @returns theme id from URL
   */
  getThemeKey = () => (this.paramMap.themeId);

  /**
   * Make sure on each url defined, id of Layout should be defined under "LayoutId"
   * @returns Layout id from URL
   */
  getLayoutKey = () => (this.paramMap.layoutId);

  /**
   * Make sure on each url defined, id of dashboard should be defined under "dashboardId"
   * @returns dashboard id from URL
   */
  getDashboardKey = () => (this.paramMap.dashboardId);

  /**
   * Current Route
   * @returns url
   */
  currentRoute = () => this.router.url;

  /**
   * By default Angular doesn't reloads if params changes
   * /app/122/345    ->     /app/122/432
   * In this case Angular doesn't opens new route in order to reuse it
   * we need to forcefully do that
   * @param initFunction initializing function
   */
  fork = (initFunction: Function) =>
    this.change().subscribe(() => {
      initFunction();
    });

  /**
   * Navigate to URL
   * @param commands array if routes
   * @returns navigate object
   */
  navigate = (commands: any[], extras?: NavigationExtras) => {
    // Ensure base segment exists and normalize root values
    if (
      commands[0] === RouteService.ROOT ||
      commands[0] === '' ||
      commands[0] === null ||
      commands[0] === undefined
    ) {
      commands[0] = RouteService.APPS;
    }
    // Normalize 'apps' to '/apps' so navigation is absolute, not relative
    if (commands[0] === RouteService.SEGMENT_APPS) {
      commands[0] = RouteService.APPS;
    }
    // If first segment is already '/apps', do not allow a second 'apps' segment right after
    if (commands[0] === RouteService.APPS && commands[1] === RouteService.SEGMENT_APPS) {
      commands.splice(1, 1);
    }
    return this.router.navigate(commands, extras);
  };

  /**
   * One level up
   */
  back = () => this.location.back();

  /**
   * @returns array of route to help redirect to last route where logout invoked
   */
  redirectTo = () => {
    const redirecToInUrl = this.getParam('redirectTo') as string | null;
    const decoded = redirecToInUrl ? decodeURIComponent(redirecToInUrl) : null;
    const route = decoded?.split('/').filter((r) => r) || [];
    if (!route?.length) route.push('');
    return route;
  };

  /**
   *
   * @returns url of redirectTo
   */
  redirectToUrl = (url: string) => {
    const decoded = url ? decodeURIComponent(url) : '';
    const route = decoded.split('/').filter((r) => r) || [];
    if (!route?.length) route.push('');
    return route;
  };

  /**
   * Change title of page
   * @param title to set
   */
  changeTitle = (title: string) => {
    this.title.setTitle(title);
  };

  /**
   * Extracts appId from URL pattern '/apps/:appId/...'
   * Acts as a resilient fallback when param parsing fails on deep routes
   */
  private extractAppIdFromUrl(url: string): string | null {
    try {
      const path = url.split('?')[0];
      const segments = path.split('/').filter((s) => !!s);
      const appsIndex = segments.indexOf(RouteService.SEGMENT_APPS);
      if (appsIndex !== RouteService.NOT_FOUND && segments.length > appsIndex + 1) {
        const candidate = segments[appsIndex + 1];
        return candidate && candidate !== RouteService.SEGMENT_APPS ? candidate : null;
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Use redirect if want to load current route but with
   * different URL params (See Report Details component for implementation)
   * @param uri
   */
  redirect = (uri: string) => {
    if (uri === RouteService.ROOT || uri === '' || uri === null || uri === undefined) {
      uri = RouteService.APPS;
    }
    // Normalize any accidental '/apps/apps' duplication
    uri = uri.replace(/\/apps\/apps/, RouteService.APPS);
    this.router.navigateByUrl(uri);
  };

  /**
   * returns queryParamMap by name
   * @param param
   */
  getParam = (param: string) => {
    // Prefer tracked queryParams captured on navigation events; fallback to snapshot
    const tracked = this.queryParams?.[param];
    const snap = this.route.snapshot.queryParamMap.get(param);
    return tracked ?? snap;
  };
}
